import React, { useEffect, useState } from 'react'
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'

import SingleSlider from 'Components/Common/SingleSlider'
import {
    CreatorContactsFilters,
    Platform,
    PlatformLabel,
} from 'types/creators'
import './AddCreatorContactModal.scss'

const ENGAGEMENT_SCORE_MIN = 0
const ENGAGEMENT_SCORE_MAX = 20

interface CreatorFiltersFormState {
    platform: Platform
    average_views: string
    followers_count: string
    engagement_score: number
}

export interface CreatorFiltersModalProps {
    isOpen: boolean
    onClose: () => void
    currentFilters: CreatorContactsFilters
    onApply: (filters: CreatorContactsFilters) => void
}

const filtersToForm = (filters: CreatorContactsFilters): CreatorFiltersFormState => ({
    platform: (filters.platform as Platform) ?? Platform.ALL,
    average_views:
        filters.average_followers != null ? String(filters.average_followers) : '',
    followers_count:
        filters.followers_count != null ? String(filters.followers_count) : '',
    engagement_score: filters.engagement_score ?? ENGAGEMENT_SCORE_MIN,
})

const buildAppliedFilters = (
    currentFilters: CreatorContactsFilters,
    form: CreatorFiltersFormState
): CreatorContactsFilters => {
    const next: CreatorContactsFilters = { ...currentFilters, page: 1 }

    if (form.platform === Platform.ALL) {
        delete next.platform
    } else {
        next.platform = form.platform
    }

    const averageViews = form.average_views.trim()
    if (averageViews) {
        next.average_followers = Number(averageViews)
    } else {
        delete next.average_followers
    }

    const followersCount = form.followers_count.trim()
    if (followersCount) {
        next.followers_count = Number(followersCount)
    } else {
        delete next.followers_count
    }

    if (form.engagement_score > ENGAGEMENT_SCORE_MIN) {
        next.engagement_score = form.engagement_score
    } else {
        delete next.engagement_score
    }

    return next
}

export const clearCreatorAdvancedFilters = (
    filters: CreatorContactsFilters
): CreatorContactsFilters => {
    const next: CreatorContactsFilters = { ...filters, page: 1 }
    delete next.platform
    delete next.average_views
    delete next.average_followers
    delete next.followers_count
    delete next.engagement_score
    return next
}

const CreatorFiltersModal: React.FC<CreatorFiltersModalProps> = ({
    isOpen,
    onClose,
    currentFilters,
    onApply,
}) => {
    const [form, setForm] = useState<CreatorFiltersFormState>(() =>
        filtersToForm(currentFilters)
    )

    useEffect(() => {
        if (isOpen) {
            setForm(filtersToForm(currentFilters))
        }
    }, [isOpen, currentFilters])

    const handleNumericChange = (
        key: 'average_views' | 'followers_count',
        value: string
    ) => {
        setForm((prev) => ({ ...prev, [key]: value.replace(/\D/g, '') }))
    }

    const handleReset = () => {
        onApply(clearCreatorAdvancedFilters(currentFilters))
        onClose()
    }

    const handleApply = () => {
        onApply(buildAppliedFilters(currentFilters, form))
        onClose()
    }

    return (
        <Modal isOpen={isOpen} toggle={onClose} centered className="creator-filters-modal">
            <ModalHeader toggle={onClose}>Filters</ModalHeader>
            <ModalBody>
                <div className="creator-filters-field">
                    <label className="creator-filters-label" htmlFor="creator-filter-platform">
                        Platform
                    </label>
                    <select
                        id="creator-filter-platform"
                        className="form-select"
                        value={form.platform}
                        onChange={(e) =>
                            setForm((prev) => ({
                                ...prev,
                                platform: e.target.value as Platform,
                            }))
                        }
                    >
                        {Object.values(Platform).map((platform) => (
                            <option key={platform} value={platform}>
                                {PlatformLabel[platform]}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="creator-filters-field">
                    <label className="creator-filters-label" htmlFor="creator-filter-average-views">
                        Minimum average views
                    </label>
                    <input
                        id="creator-filter-average-views"
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        placeholder="e.g. 10000"
                        value={form.average_views}
                        onChange={(e) => handleNumericChange('average_views', e.target.value)}
                    />
                </div>
                <div className="creator-filters-field">
                    <label className="creator-filters-label" htmlFor="creator-filter-followers-count">
                        Minimum followers
                    </label>
                    <input
                        id="creator-filter-followers-count"
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        placeholder="e.g. 50000"
                        value={form.followers_count}
                        onChange={(e) => handleNumericChange('followers_count', e.target.value)}
                    />
                </div>
                <div className="creator-filters-field">
                    <label className="creator-filters-label" htmlFor="creator-filter-engagement">
                        Minimum engagement score
                    </label>
                    <SingleSlider
                        min={ENGAGEMENT_SCORE_MIN}
                        max={ENGAGEMENT_SCORE_MAX}
                        value={form.engagement_score}
                        onChange={(engagement_score) =>
                            setForm((prev) => ({ ...prev, engagement_score }))
                        }
                    />
                    <p className="creator-filters-hint mb-0">
                        {form.engagement_score === ENGAGEMENT_SCORE_MIN
                            ? 'No minimum — drag to set a minimum score'
                            : `Minimum score: ${form.engagement_score}`}
                    </p>
                </div>
            </ModalBody>
            <ModalFooter>
                <button
                    type="button"
                    className="theme-btn theme-btn-lg w-100 btn-primary-outlined"
                    onClick={handleReset}
                >
                    Reset
                </button>
                <button
                    type="button"
                    className="theme-btn theme-btn-lg w-100 btn-primary"
                    onClick={handleApply}
                >
                    Apply
                </button>
            </ModalFooter>
        </Modal>
    )
}

export default CreatorFiltersModal
